import React from 'react';
import { Sparkles, Phone, MessageCircle, ExternalLink, Heart, MapPin, Clock, Shield } from 'lucide-react';
import { contactInfo, services, seoKeywords } from '../data/mock';

// Constants
const MAX_SEO_KEYWORDS = 25;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black border-t border-amber-500/20">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-6 sm:mb-8">
              <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-amber-500" />
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-amber-500">RETOUR AFFECTIF</h3>
                <p className="text-xs sm:text-sm text-amber-500/70 font-medium">RAPIDE & PUISSANT</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
              Le <strong>grand marabout africain</strong> est le spécialiste reconnu du <strong>retour affectif rapide</strong>. 
              Avec plus de 30 ans d'expérience, il vous aide à <strong>récupérer votre ex</strong> et retrouver l'amour. 
              Magie blanche, noire et rouge.
            </p>
            <div className="flex space-x-3 sm:space-x-4">
              <a
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500/10 rounded-full flex items-center justify-center hover:bg-amber-500/20 transition-colors"
                aria-label="Appeler le marabout"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
              </a>
              <a
                href={contactInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 sm:w-12 sm:h-12 bg-green-600/20 rounded-full flex items-center justify-center hover:bg-green-600/30 transition-colors"
                aria-label="Contacter le marabout sur WhatsApp"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-white font-bold text-lg sm:text-xl mb-4 sm:mb-8">Services Retour Affectif</h4>
            <ul className="space-y-2 sm:space-y-4">
              {services.map((service) => (
                <li key={service.id}>
                  <a
                    href="#services"
                    className="text-gray-300 hover:text-amber-500 transition-colors text-sm sm:text-base flex items-center"
                  >
                    <Heart className="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-amber-500/50 flex-shrink-0" />
                    <span className="truncate">{service.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Links Column - Important pour SEO */}
          <div>
            <h4 className="text-white font-bold text-lg sm:text-xl mb-4 sm:mb-8">Nos Services</h4>
            <ul className="space-y-2 sm:space-y-4">
              <li className="text-gray-300 text-sm sm:text-base flex items-center">
                <Heart className="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-amber-500/50 flex-shrink-0" />
                <span>Retour affectif rapide</span>
              </li>
              <li className="text-gray-300 text-sm sm:text-base flex items-center">
                <Heart className="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-amber-500/50 flex-shrink-0" />
                <span>Récupérer son ex</span>
              </li>
              <li className="text-gray-300 text-sm sm:text-base flex items-center">
                <Heart className="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-amber-500/50 flex-shrink-0" />
                <span>Magie blanche, noire, rouge</span>
              </li>
              <li className="text-gray-300 text-sm sm:text-base flex items-center">
                <Heart className="w-3 h-3 sm:w-4 sm:h-4 mr-2 sm:mr-3 text-amber-500/50 flex-shrink-0" />
                <span>Envoûtement amoureux</span>
              </li>
            </ul>
            
            {/* Zones d'intervention pour SEO local */}
            <h4 className="text-white font-bold text-lg sm:text-xl mt-6 sm:mt-10 mb-4 sm:mb-6">Zones d'intervention</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-center"><MapPin className="w-3 h-3 mr-2 text-amber-500/50" />France (Paris, Lyon, Marseille...)</li>
              <li className="flex items-center"><MapPin className="w-3 h-3 mr-2 text-amber-500/50" />Belgique (Bruxelles, Liège...)</li>
              <li className="flex items-center"><MapPin className="w-3 h-3 mr-2 text-amber-500/50" />Suisse (Genève, Lausanne...)</li>
              <li className="flex items-center"><MapPin className="w-3 h-3 mr-2 text-amber-500/50" />Canada (Montréal, Québec...)</li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-white font-bold text-lg sm:text-xl mb-4 sm:mb-8">Contact Direct</h4>
            <div className="space-y-4 sm:space-y-6">
              <div>
                <p className="text-amber-500 font-bold text-lg sm:text-xl">{contactInfo.name}</p>
                <p className="text-gray-300 text-sm sm:text-base">Grand Marabout Africain</p>
                <p className="text-gray-400 text-sm">Spécialiste Retour Affectif</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm sm:text-base mb-1">Téléphone & WhatsApp</p>
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-amber-500 font-bold text-base sm:text-xl hover:text-amber-400 transition-colors">
                  {contactInfo.phone}
                </a>
              </div>
              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                <Clock className="w-4 h-4 text-amber-500/50" />
                <span>Disponible 7j/7 - 24h/24</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                <Shield className="w-4 h-4 text-amber-500/50" />
                <span>Confidentialité garantie</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEO Keywords Section - Très important pour le référencement */}
      <div className="border-t border-amber-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          <h5 className="text-gray-500 text-xs sm:text-sm uppercase tracking-wider mb-4 sm:mb-6 font-medium">Mots-clés Retour Affectif</h5>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {seoKeywords.slice(0, MAX_SEO_KEYWORDS).map((keyword, index) => (
              <span
                key={index}
                className="px-2 sm:px-4 py-1 sm:py-2 bg-gray-900/50 border border-amber-500/10 rounded-full text-gray-400 text-xs sm:text-sm hover:text-amber-500 hover:border-amber-500/30 transition-colors cursor-default"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-amber-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
            <p className="text-gray-400 text-xs sm:text-base text-center sm:text-left">
              © {currentYear} <strong>Retour Affectif Rapide</strong> | Grand Marabout Africain | Tous droits réservés
            </p>
            <div className="flex items-center space-x-4 sm:space-x-8">
              <a href="#faq" className="text-gray-400 hover:text-amber-500 text-xs sm:text-base transition-colors">
                FAQ
              </a>
              <a href="#temoignages" className="text-gray-400 hover:text-amber-500 text-xs sm:text-base transition-colors">
                Témoignages
              </a>
              <a href="#contact" className="text-gray-400 hover:text-amber-500 text-xs sm:text-base transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
